// crewbit-admin/src/components/money/HeaderQuickActionsButton.tsx
//
// SPEC §Overview.7.2 + §0.3. The "+" button + popover surface for the seven
// Money home verbs. v1 ships ONE working route (new-invoice → existing
// /money/invoices?action=new-invoice wiring at MoneyInvoicesPage URL effect);
// the other six verbs surface a calm "Coming soon" toast pointing operators
// to mobile or to the dedicated session destination.
//
// Self-contained: owns its own popover state, click-outside listener, and
// toast state. Parent only mounts <HeaderQuickActionsButton />.
//
// Cmd+K palette is deferred (§AdminCmdKPalette / Session 119 §2.6) — header
// `+` is the v1 affordance.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FilePlus,
  Zap,
  DollarSign,
  Receipt,
  ScanLine,
  FileText,
  UserPlus,
  Calculator,
} from 'lucide-react';
import { Toast } from '../common/Toast';
import { palette, shadow, transition, radii, spacing } from '../../styles/tokens';
import { AddVendorPanel } from './AddVendorPanel';
import { VendorBillPanel } from './VendorBillPanel';
import { QuickBillPanel } from './QuickBillPanel';
import { RecordPaymentPanel } from './RecordPaymentPanel';
import { ScanReceiptPanel } from './ScanReceiptPanel';

type ModalKey = 'quick-bill' | 'record-payment' | 'scan-receipt' | 'vendor-invoice' | 'add-vendor';

interface VerbDef {
  key: string;
  label: string;
  hint: string;
  Icon: typeof Plus;
  destination:
    | { kind: 'route'; path: string }
    | { kind: 'modal'; modal: ModalKey }
    | { kind: 'soon'; toastMessage: string };
}

const VERBS: VerbDef[] = [
  {
    key: 'new-invoice',
    label: 'New invoice',
    hint: 'Bill a client',
    Icon: FilePlus,
    destination: { kind: 'route', path: '/money/invoices?action=new-invoice' },
  },
  {
    key: 'quick-bill',
    label: 'Quick bill',
    hint: 'One-line invoice',
    Icon: Zap,
    destination: { kind: 'modal', modal: 'quick-bill' },
  },
  {
    key: 'record-payment',
    label: 'Record payment',
    hint: 'Mark money received',
    Icon: DollarSign,
    destination: { kind: 'modal', modal: 'record-payment' },
  },
  {
    key: 'log-expense',
    label: 'Log expense',
    hint: 'Add a business expense',
    Icon: Receipt,
    destination: { kind: 'route', path: '/money/expenses?action=log' },
  },
  {
    key: 'scan-receipt',
    label: 'Scan receipt',
    hint: 'Capture a paper receipt',
    Icon: ScanLine,
    destination: { kind: 'modal', modal: 'scan-receipt' },
  },
  {
    key: 'vendor-invoice',
    label: 'Vendor invoice',
    hint: 'Record a bill from a vendor',
    Icon: FileText,
    destination: { kind: 'modal', modal: 'vendor-invoice' },
  },
  {
    key: 'add-vendor',
    label: 'Add vendor',
    hint: 'New supplier',
    Icon: UserPlus,
    destination: { kind: 'modal', modal: 'add-vendor' },
  },
  {
    key: 'generate-tax-summary',
    label: 'Generate tax summary',
    hint: 'CRA-ready HST summary',
    Icon: Calculator,
    destination: { kind: 'route', path: '/money/tax?action=generate-summary' },
  },
];

export function HeaderQuickActionsButton() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'info' | 'success' | 'error' } | null>(null);
  const [activeModal, setActiveModal] = useState<ModalKey | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [hoveredVerb, setHoveredVerb] = useState<string | null>(null);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function handleVerbClick(verb: VerbDef) {
    setOpen(false);
    if (verb.destination.kind === 'route') {
      void navigate(verb.destination.path);
    } else if (verb.destination.kind === 'modal') {
      setActiveModal(verb.destination.modal);
    } else {
      setToast({ msg: verb.destination.toastMessage, type: 'info' });
    }
  }

  const onModalResult = (ok: boolean, message: string) => {
    setToast({ msg: message, type: ok ? 'success' : 'error' });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Quick actions"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          borderRadius: radii.sm,
          border: 'none',
          cursor: 'pointer',
          background: hovered ? palette.accentDeep : palette.accent,
          color: palette.white,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          transition: `background ${transition.fast}`,
          boxShadow: shadow.xs,
        }}
      >
        <Plus size={16} strokeWidth={2.25} />
        <span>New</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 280,
            background: palette.white,
            border: `1px solid ${palette.g150}`,
            borderRadius: radii.md,
            boxShadow: shadow.lg,
            padding: spacing.xs,
            zIndex: 50,
          }}
        >
          {VERBS.map((verb) => {
            const isHover = hoveredVerb === verb.key;
            return (
              <button
                key={verb.key}
                type="button"
                role="menuitem"
                onClick={() => handleVerbClick(verb)}
                onMouseEnter={() => setHoveredVerb(verb.key)}
                onMouseLeave={() => setHoveredVerb(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  width: '100%',
                  padding: '8px 10px',
                  border: 'none',
                  background: isHover ? palette.g100 : 'transparent',
                  borderRadius: radii.sm,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  transition: `background ${transition.fast}`,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: radii.sm,
                    background: palette.accentWash,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <verb.Icon size={15} color={palette.accent} strokeWidth={2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>
                    {verb.label}
                  </div>
                  <div style={{ fontSize: 11.5, color: palette.g500, marginTop: 1 }}>
                    {verb.hint}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}

      <QuickBillPanel
        isOpen={activeModal === 'quick-bill'}
        onClose={() => setActiveModal(null)}
        onResult={onModalResult}
      />
      <RecordPaymentPanel
        isOpen={activeModal === 'record-payment'}
        onClose={() => setActiveModal(null)}
        onResult={onModalResult}
      />
      <ScanReceiptPanel
        isOpen={activeModal === 'scan-receipt'}
        onClose={() => setActiveModal(null)}
        onResult={onModalResult}
      />
      <VendorBillPanel
        isOpen={activeModal === 'vendor-invoice'}
        onClose={() => setActiveModal(null)}
        onResult={onModalResult}
      />
      <AddVendorPanel
        isOpen={activeModal === 'add-vendor'}
        onClose={() => setActiveModal(null)}
        onResult={onModalResult}
      />
    </div>
  );
}
